import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { useApp } from '@verlo/shared/contexts/AppContext';
import { cashApi } from '@verlo/shared/services/api';
import { ArrowLeft, Printer, RefreshCw } from 'lucide-react';
import { useDrawer } from '../contexts/DrawerContext';
import { OpenTillDialog, MovementDialog, CashOffSheet, ZRead, MOVEMENTS, PrintersPanel } from '../components/till';
import { sinceWords } from '@verlo/shared/lib/duration';

// The till (cash-off wave 1, 18 Sep 2026). One page for the drawer: open it with the
// float, the movements through it during service, the cash-off at the end, and the Z read
// after. Only a terminal with a drawer has it — the floor tills at Yard & Coop don't.

const clock = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const KIND_WORD = { paid_out: 'Paid out', safe_drop: 'Safe drop', float_top_up: 'Top-up', tips_out: 'Tips out', no_sale: 'No sale' };
const KIND_SIGN = { paid_out: '−', safe_drop: '−', tips_out: '−', float_top_up: '+', no_sale: '' };

const Tile = ({ label, value, testId, tone }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={`${testId}-tile`}>
    <p className="text-[11px] uppercase tracking-wide text-ink-faint">{label}</p>
    <p className={`mt-1 font-mono text-3xl font-semibold tabular-nums ${tone || 'text-ink'}`} data-testid={testId}>{value}</p>
  </div>
);

/** The Z read for one session, with Print. `/till/z/:sessionId`. */
export const ZReadPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = useApp();
  const [session, setSession] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    cashApi.session(sessionId).then(({ data }) => setSession(data)).catch(() => setErr('Could not load that read'));
  }, [sessionId]);
  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="z-read-page">
      <div className="no-print flex items-center justify-between">
        <button type="button" onClick={() => navigate('/till')} className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink"><ArrowLeft size={15} />Till</button>
        <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="z-read-print"><Printer size={14} className="mr-1.5" />Print</Button>
      </div>
      <div className="rounded-xl border bg-white p-5">
        {err && <p className="text-sm text-signal-stop">{err}</p>}
        {!err && !session && <p className="text-sm text-ink-faint">Loading…</p>}
        {session && <ZRead session={session} formatCurrency={formatCurrency} />}
      </div>
    </div>
  );
};

const TillPage = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useApp();
  const { drawer, loaded, hasDrawer, session, canManage, refresh } = useDrawer();
  const [openDialog, setOpenDialog] = useState(false);
  const [movement, setMovement] = useState(null);       // a kind, while the dialog is open
  const [cashOff, setCashOff] = useState(false);

  useEffect(() => { refresh(); }, [refresh]);

  if (loaded && !hasDrawer) {
    return (
      <div className="mx-auto max-w-3xl" data-testid="till-page">
        <Notice tone="info" word="Till" title="This till has no cash drawer">
          Cash is taken at the bar. Card payments and refunds work here as usual.
        </Notice>
      </div>
    );
  }

  const exp = session?.expected || {};
  const movements = session?.movements || [];
  const last = drawer?.last_closed;

  return (
    <div className="mx-auto max-w-5xl space-y-4" data-testid="till-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Till</h1>
          <p className="text-[12px] text-ink-faint">
            {drawer?.terminal_label && <>{drawer.terminal_label} · </>}
            {session
              ? <>Open since {sinceWords(session.opened_at)}{session.opened_by_name ? ` · ${session.opened_by_name}` : ''} · {formatCurrency(session.float_opening)} float</>
              : last ? <>Cashed off {clock(last.closed_at)} · Z {last.z_number}</> : 'Not opened yet'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={refresh} className="text-ink-faint hover:text-ink-soft" title="Refresh"><RefreshCw size={14} /></button>
          {session ? (
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setCashOff(true)} data-testid="till-cash-off">Cash off</Button>
          ) : (
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setOpenDialog(true)} data-testid="till-open">Open till</Button>
          )}
        </div>
      </div>

      {!session && last && (
        <Notice tone="info" word="Z read" title={`Last session · Z ${last.z_number} · ${last.trading_date}`}
          action={<Button variant="outline" size="sm" onClick={() => navigate(`/till/z/${last.id}`)} data-testid="till-last-z">Open</Button>}>
          {last.variance === 0 ? 'Balanced.' : `${last.variance > 0 ? 'Over' : 'Short'} ${formatCurrency(Math.abs(last.variance))}.`} Float carried {formatCurrency(last.float_carried)}.
        </Notice>
      )}

      {session && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Tile label="Expected in drawer" value={formatCurrency(exp.expected)} testId="till-expected" />
            <Tile label="Cash sales" value={formatCurrency(exp.cash_sales)} testId="till-cash-sales" />
            <Tile label="Card takings" value={formatCurrency((session.summary?.takings?.card || 0) + (session.summary?.card_tips || 0))} testId="till-card" />
            <Tile label="Safe" value={formatCurrency(drawer?.safe_balance)} testId="till-safe"
              tone={drawer?.settings?.safe_limit && drawer.safe_balance > drawer.settings.safe_limit ? 'text-signal-warn' : undefined} />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div className="rounded-xl border bg-white p-4 md:col-span-2" data-testid="till-actions">
              <p className="mb-2 text-[11px] uppercase tracking-wide text-ink-faint">Cash movements</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MOVEMENTS).map(([kind, m]) => (
                  <Button key={kind} variant="outline" className="h-11 justify-start" onClick={() => setMovement(kind)} data-testid={`till-movement-${kind}`}>
                    {m.title}
                  </Button>
                ))}
              </div>
              {!canManage && <p className="mt-2 text-[11px] text-ink-faint">Safe drops, top-ups and tips out need a manager's PIN.</p>}
            </div>

            <div className="rounded-xl border bg-white p-4 md:col-span-3">
              <p className="mb-2 text-[11px] uppercase tracking-wide text-ink-faint">This session</p>
              <div className="divide-y divide-hairline text-[13.5px]">
                <div className="flex justify-between py-1 text-ink-soft"><span>Opening float</span><span className="font-mono tabular-nums">{formatCurrency(exp.float_opening)}</span></div>
                <div className="flex justify-between py-1 text-ink-soft"><span>Cash sales</span><span className="font-mono tabular-nums">+{formatCurrency(exp.cash_sales)}</span></div>
                {exp.cash_tips > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Cash tips taken</span><span className="font-mono tabular-nums">+{formatCurrency(exp.cash_tips)}</span></div>}
                {exp.float_top_ups > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Float top-ups</span><span className="font-mono tabular-nums">+{formatCurrency(exp.float_top_ups)}</span></div>}
                {exp.cash_refunds > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Cash refunds</span><span className="font-mono tabular-nums">−{formatCurrency(exp.cash_refunds)}</span></div>}
                {exp.paid_outs > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Paid out</span><span className="font-mono tabular-nums">−{formatCurrency(exp.paid_outs)}</span></div>}
                {exp.safe_drops > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Safe drops</span><span className="font-mono tabular-nums">−{formatCurrency(exp.safe_drops)}</span></div>}
                {exp.tips_out > 0 && <div className="flex justify-between py-1 text-ink-soft"><span>Tips out</span><span className="font-mono tabular-nums">−{formatCurrency(exp.tips_out)}</span></div>}
                <div className="flex justify-between py-1 font-semibold text-ink"><span>Expected</span><span className="font-mono tabular-nums">{formatCurrency(exp.expected)}</span></div>
              </div>
              <p className="mt-3 mb-1 text-[11px] uppercase tracking-wide text-ink-faint">Movements</p>
              {movements.length === 0 ? (
                <p className="text-sm text-ink-faint">None yet.</p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto" data-testid="till-movements">
                  {movements.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-xs">
                      <span className="shrink-0 tabular-nums text-ink-faint">{clock(m.created_at)}</span>
                      <Signal tone={m.kind === 'float_top_up' ? 'ok' : m.kind === 'no_sale' ? 'info' : 'warn'} dot={false}>{KIND_WORD[m.kind] || m.kind}</Signal>
                      <span className="min-w-0 flex-1 truncate text-ink-soft">{m.note || m.by_name}</span>
                      <span className="shrink-0 font-mono tabular-nums text-ink">{m.kind === 'no_sale' ? '' : `${KIND_SIGN[m.kind]}${formatCurrency(m.amount)}`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4">
            <ZRead session={session} formatCurrency={formatCurrency} compact />
          </div>
        </>
      )}

      {/* The printers this till dials (#94). Here rather than in the office because the
          spool is the till's: only this machine knows whether a printer took the ticket. */}
      <PrintersPanel />

      {!session && loaded && !last && (
        <div className="rounded-xl border bg-white p-6 text-center text-sm text-ink-soft">
          Open the till with the float before taking cash. A cash payment on a closed till opens it with the carried float.
        </div>
      )}

      <OpenTillDialog open={openDialog} onOpenChange={setOpenDialog} formatCurrency={formatCurrency} />
      <MovementDialog open={!!movement} kind={movement || 'paid_out'} onOpenChange={(v) => { if (!v) setMovement(null); }} formatCurrency={formatCurrency} />
      <CashOffSheet open={cashOff} onOpenChange={setCashOff} formatCurrency={formatCurrency} onClosed={(s) => navigate(`/till/z/${s.id}`)} />
    </div>
  );
};

export default TillPage;
